(() => {
  'use strict';

  const define = (name, constructor) => {
    if (!window.customElements.get(name)) window.customElements.define(name, constructor);
  };
  const delay = (callback, wait = 300) => {
    let timer;
    return (...args) => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => callback(...args), wait);
    };
  };
  const cartAjaxUrl = (url) => {
    const value = String(url || '').trim();
    if (!value) throw new Error(window.cartStrings?.error || 'Cart request failed');
    const match = value.match(/^([^?#]*)(.*)$/);
    const path = match?.[1] || value;
    const suffix = match?.[2] || '';
    return /\.(?:js|json)$/i.test(path) ? value : `${path}.js${suffix}`;
  };
  const postJson = (url, body) => fetch(cartAjaxUrl(url), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  const parseJsonResponse = async (response, fallbackMessage) => {
    const text = await response.text();
    try {
      return JSON.parse(text);
    } catch (_error) {
      throw new Error(fallbackMessage || window.cartStrings?.error || 'Cart request failed');
    }
  };
  // Only change/update responses contain a complete cart; add responses do not.
  const isFullCart = (state) => Array.isArray(state?.items)
    && Number.isFinite(state.item_count) && Number.isFinite(state.total_price);

  const itemProperty = (item, name) => {
    const value = item?.properties?.[name];
    return value == null ? '' : String(value);
  };
  const syncVariantSelects = (root, variant) => {
    if (!root || !variant) return;
    root.querySelectorAll('select').forEach((select, index) => {
      if (variant.options?.[index] != null) select.value = String(variant.options[index]);
    });
  };
  const resolveSelectableVariant = (root, variants, options = {}) => {
    const selected = findVariant(variants, selectedValues(root));
    if (selected && (selected.available || !options.skipUnavailable)) return selected;
    if (!selected && !options.skipNonExistent) return null;
    const fallback = variants.find((variant) => variant.available);
    if (fallback) syncVariantSelects(root, fallback);
    return fallback || null;
  };
  const parseJsonScript = (root) => {
    try {
      return JSON.parse(root.querySelector('script[type="application/json"]')?.textContent || '[]');
    } catch (_error) {
      return [];
    }
  };
  const formatMoney = (cents, format = '${{amount}}') => {
    if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents, format);
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: document.querySelector('html')?.dataset.currency || 'USD',
    }).format((Number(cents) || 0) / 100);
  };
  const selectedValues = (root) => Array.from(root.querySelectorAll('select')).map((select) => select.value);
  const findVariant = (variants, values) => variants.find((variant) =>
    values.every((value, index) => String(variant.options?.[index]) === String(value))
  );
  const dispatch = (target, name, detail = {}) => target.dispatchEvent(new CustomEvent(name, {
    bubbles: true,
    detail,
  }));

  class SearchForm extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('input[type="search"]');
      this.resetButton = this.querySelector('button[type="reset"]');
      if (!this.input) return;
      this.input.form?.addEventListener('reset', (event) => this.onReset(event));
      this.input.addEventListener('input', delay(() => this.onChange(), 250));
      this.onChange();
    }

    onChange() {
      this.resetButton?.classList.toggle('hidden', !this.input.value.length);
    }

    onReset(event) {
      event.preventDefault();
      this.input.value = '';
      this.onChange();
      this.input.focus();
    }
  }
  define('search-form', SearchForm);

  class PredictiveSearch extends SearchForm {
    constructor() {
      super();
      if (!this.input) return;
      this.results = this.querySelector('[data-predictive-search]');
      this.status = this.querySelector('.predictive-search-status');
      this.cache = new Map();
      this.controller = null;
      this.input.addEventListener('focus', () => this.input.value.trim() && this.search());
      this.input.addEventListener('input', delay(() => this.search(), 250));
      this.addEventListener('focusout', () => window.setTimeout(() => {
        if (!this.contains(document.activeElement)) this.close();
      }));
      this.addEventListener('keydown', (event) => this.onKeydown(event));
    }

    async search() {
      const term = this.input.value.trim();
      if (!term) return this.close(true);
      if (!this.results || !window.routes?.predictive_search_url) return;
      this.setAttribute('loading', '');
      if (this.status) this.status.textContent = this.dataset.loadingText || 'Loading';
      const key = term.toLocaleLowerCase();
      if (this.cache.has(key)) return this.render(this.cache.get(key));
      this.controller?.abort();
      this.controller = new AbortController();
      try {
        const response = await fetch(`${window.routes.predictive_search_url}?q=${encodeURIComponent(term)}&section_id=predictive-search`, {
          signal: this.controller.signal,
        });
        if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);
        const html = await response.text();
        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        const section = documentFragment.querySelector('#shopify-section-predictive-search');
        const markup = section?.innerHTML || '';
        this.cache.set(key, markup);
        this.render(markup);
      } catch (error) {
        if (error.name !== 'AbortError') this.close();
      }
    }

    render(markup) {
      this.results.innerHTML = markup;
      this.removeAttribute('loading');
      this.setAttribute('results', 'true');
      this.setAttribute('open', 'true');
      this.input.setAttribute('aria-expanded', 'true');
      this.input.removeAttribute('aria-activedescendant');
      const liveText = this.querySelector('[data-predictive-search-live-region-count-value]')?.textContent;
      if (this.status && liveText) this.status.textContent = liveText;
    }

    close(clear = false) {
      if (clear && this.input) this.input.value = '';
      this.controller?.abort();
      this.removeAttribute('open');
      this.removeAttribute('loading');
      this.removeAttribute('results');
      this.input?.setAttribute('aria-expanded', 'false');
      this.input?.removeAttribute('aria-activedescendant');
      this.querySelectorAll('[role="option"][aria-selected="true"]').forEach((item) => item.setAttribute('aria-selected', 'false'));
    }

    onReset(event) {
      super.onReset(event);
      if (this.results) this.results.innerHTML = '';
      this.close(true);
    }

    onKeydown(event) {
      if (event.key === 'Escape') return this.close();
      if (!['ArrowDown', 'ArrowUp', 'Enter'].includes(event.key)) return;
      const options = Array.from(this.querySelectorAll('[role="option"]')).filter((item) => !item.hidden);
      if (!options.length) return;
      const active = options.findIndex((item) => item.getAttribute('aria-selected') === 'true');
      if (event.key === 'Enter') {
        const link = options[active]?.querySelector('a') || options[active];
        if (active >= 0 && link) {
          event.preventDefault();
          link.click();
        }
        return;
      }
      event.preventDefault();
      const next = event.key === 'ArrowDown'
        ? (active + 1) % options.length
        : (active <= 0 ? options.length - 1 : active - 1);
      options.forEach((item, index) => item.setAttribute('aria-selected', String(index === next)));
      if (!options[next].id) options[next].id = `predictive-search-option-${next}`;
      this.input.setAttribute('aria-activedescendant', options[next].id);
      options[next].scrollIntoView({ block: 'nearest' });
    }
  }
  define('predictive-search', PredictiveSearch);

  class CartRemoveButton extends HTMLElement {
    connectedCallback() {
      if (this.dataset.runtimeBound) return;
      this.dataset.runtimeBound = 'true';
      this.addEventListener('click', (event) => {
        event.preventDefault();
        const cartItem = this.closest('[data-line-key]');
        this.closest('cart-items, cart-drawer-items')?.updateQuantity(
          this.dataset.index,
          0,
          event,
          undefined,
          cartItem?.dataset.lineKey
        );
      });
    }
  }
  define('cart-remove-button', CartRemoveButton);

  class CartItems extends HTMLElement {
    connectedCallback() {
      if (this.dataset.runtimeBound) return;
      this.dataset.runtimeBound = 'true';
      this.addEventListener('change', delay((event) => {
        const input = event.target.closest('input[name="updates[]"], input[data-index]');
        if (!input) return;
        const cartItem = input.closest('[data-line-key]');
        this.updateQuantity(
          input.dataset.index,
          Number(input.value),
          event,
          input.name,
          input.dataset.lineKey || cartItem?.dataset.lineKey
        );
      }, 300));
    }

    getSectionsToRender() {
      if (this.tagName === 'CART-DRAWER-ITEMS') {
        return [
          { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
          { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
        ];
      }
      return [
        { id: 'main-cart-items', section: document.getElementById('main-cart-items')?.dataset.id, selector: '.js-contents' },
        { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
        { id: 'cart-live-region-text', section: 'cart-live-region-text', selector: '.shopify-section' },
        { id: 'main-cart-footer', section: document.getElementById('main-cart-footer')?.dataset.id, selector: '.js-contents' },
      ].filter((item) => item.section);
    }

    updateQuantity(line, quantity, event, _inputName, lineKey = '') {
      const task = () => this.performQuantityUpdate(line, quantity, lineKey);
      const drawer = this.closest('cart-drawer') || document.querySelector('cart-drawer');
      if (drawer?.enqueueCartMutation) return drawer.enqueueCartMutation(task);

      const previous = this.cartMutationQueue || Promise.resolve();
      const tracked = previous.catch(() => undefined).then(task);
      this.cartMutationQueue = tracked;
      const clear = () => {
        if (this.cartMutationQueue === tracked) this.cartMutationQueue = null;
      };
      tracked.then(clear, clear);
      return tracked;
    }

    async performQuantityUpdate(line, quantity, lineKey = '') {
      const sections = this.getSectionsToRender();
      this.setLoading(line, true);
      try {
        const payload = {
          quantity: Number(quantity),
          sections: sections.map((item) => item.section),
          sections_url: window.location.pathname,
        };
        if (lineKey) payload.id = String(lineKey);
        else payload.line = Number(line);
        const response = await postJson(window.routes.cart_change_url, payload);
        const state = await parseJsonResponse(response, window.cartStrings?.error || 'Cart update failed');
        if (!response.ok || state.errors) {
          const message = typeof state.errors === 'string'
            ? state.errors
            : state.description || state.message || window.cartStrings?.error || 'Cart update failed';
          this.restoreQuantityInput(line, quantity);
          this.setLiveMessage(line, message);
          const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
          if (errors) errors.textContent = message;
          dispatch(this, 'cart:error', { message });
          return;
        }
        this.classList.toggle('is-empty', state.item_count === 0);
        document.querySelector('cart-drawer')?.classList.toggle('is-empty', state.item_count === 0);
        sections.forEach((section) => this.replaceSection(section, state.sections?.[section.section]));
        this.setLiveMessage(line, '');
        if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: state });
        }
        dispatch(this, 'cart:updated', { cart: state, line, quantity });
      } catch (_error) {
        console.error(_error);
        this.restoreQuantityInput(line, quantity);
        this.setLiveMessage(line, '');
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = window.cartStrings?.error || 'There was an error updating your cart. Please try again.';
      } finally {
        this.setLoading(line, false);
      }
    }

    restoreQuantityInput(line, attemptedQuantity) {
      const input = Array.from(this.querySelectorAll('input[name="updates[]"], input[data-index]'))
        .find((candidate) => Number(candidate.dataset.index) === Number(line));
      // The rendered default is the last server-confirmed quantity.
      // Do not overwrite a newer edit made while this request was pending.
      if (input && Number(input.value) === Number(attemptedQuantity)) input.value = input.defaultValue;
    }

    replaceSection(section, html) {
      if (!html) return;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const source = parsed.querySelector(section.selector) || parsed.body.firstElementChild;
      const host = document.getElementById(section.id);
      const target = host?.querySelector(section.selector) || host || document.querySelector(section.selector);
      if (source && target) {
        if (section.section === 'cart-drawer') {
          document.querySelector('cart-drawer')?.preserveUpsellState?.(source, target);
        }
        target.innerHTML = source.innerHTML;
      }
    }

    setLiveMessage(line, message) {
      const lineError = document.querySelector(`#Line-item-error-${line} .cart-item__error-text, #CartDrawer-LineItemError-${line} .cart-item__error-text`);
      if (lineError) lineError.textContent = message;
      const live = document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
      if (live) {
        live.textContent = message;
        live.setAttribute('aria-hidden', String(!message));
      }
    }

    setLoading(line, loading) {
      this.classList.toggle('cart__items--disabled', loading);
      this.querySelectorAll(`#CartItem-${line} .loading__spinner, #CartDrawer-Item-${line} .loading__spinner`).forEach((spinner) => {
        spinner.classList.toggle('hidden', !loading);
      });
    }
  }
  define('cart-items', CartItems);
  define('cart-drawer-items', class CartDrawerItems extends CartItems {});

  class CartNote extends HTMLElement {
    connectedCallback() {
      if (this.dataset.runtimeBound) return;
      this.dataset.runtimeBound = 'true';
      this.addEventListener('input', delay(async (event) => {
        const response = await postJson(window.routes.cart_update_url, { note: event.target.value });
        if (!response.ok) dispatch(this, 'cart:error', { message: 'Cart note update failed' });
      }, 300));
    }
  }
  define('cart-note', CartNote);

  class CartDiscountForm extends HTMLElement {
    connectedCallback() {
      if (this.dataset.runtimeBound) return;
      this.dataset.runtimeBound = 'true';
      this.form = this.querySelector('form');
      this.input = this.querySelector('input[name="cart-discount-field"]');
      this.error = this.querySelector('.cart-discount-form__error');
      this.submitButton = this.querySelector('[type="submit"]');
      this.form?.addEventListener('submit', (event) => this.submit(event));
      this.input?.addEventListener('input', () => this.showError(''));
    }

    showError(message) {
      if (!this.error) return;
      this.error.textContent = message || this.dataset.errorMessage || '';
      this.error.style.display = message ? 'block' : '';
    }

    async submit(event) {
      event.preventDefault();
      const discount = this.input?.value.trim() || '';
      if (!discount) {
        this.showError(this.dataset.errorMessage || 'Please enter a discount code.');
        this.input?.focus();
        return;
      }

      this.showError('');
      this.form?.setAttribute('aria-busy', 'true');
      if (this.submitButton) this.submitButton.disabled = true;
      try {
        const response = await postJson(window.routes.cart_update_url, {
          discount,
          sections: ['cart-drawer', 'cart-icon-bubble'],
          sections_url: window.location.pathname,
        });
        const state = await parseJsonResponse(response, 'Discount code could not be applied.');
        if (!response.ok || state.errors) throw new Error(state.errors || 'Discount code could not be applied.');
        const discountCodes = Array.isArray(state.discount_codes) ? state.discount_codes : [];
        const matchedCode = discountCodes.find((item) => String(item.code || '').toLowerCase() === discount.toLowerCase());
        if (!matchedCode || matchedCode.applicable === false) throw new Error('This discount is not available for the current cart.');

        const drawer = document.querySelector('cart-drawer');
        drawer?.renderContents(state);
        dispatch(document, 'cart:updated', { cart: state, source: 'cart-discount' });
      } catch (error) {
        this.showError(error.message || this.dataset.errorMessage || 'Discount code could not be applied.');
      } finally {
        this.form?.removeAttribute('aria-busy');
        if (this.submitButton) this.submitButton.disabled = false;
      }
    }
  }
  define('cart-discount-form', CartDiscountForm);

  class CartDrawer extends HTMLElement {
    connectedCallback() {
      if (this.dataset.runtimeBound) return;
      this.dataset.runtimeBound = 'true';
      this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', () => this.close());
      this.addEventListener('keyup', (event) => event.key === 'Escape' && this.close());
      this.addEventListener('submit', (event) => this.onCheckoutSubmit(event));
      this.onCartUpdated = (event) => {
        if (event.detail?.source === 'cart-drawer-upsells') return;
        window.queueMicrotask(() => this.syncPreselectedUpsells());
      };
      this.onCartError = (event) => this.showCartError(event.detail?.message);
      document.addEventListener('cart:updated', this.onCartUpdated);
      this.addEventListener('cart:error', this.onCartError);
      const trigger = document.getElementById('cart-icon-bubble');
      if (trigger) {
        trigger.setAttribute('aria-haspopup', 'dialog');
        trigger.addEventListener('click', (event) => {
          event.preventDefault();
          this.open(trigger);
        });
      }
      window.queueMicrotask(() => this.syncPreselectedUpsells());
    }

    open(trigger) {
      this.activeElement = trigger || document.activeElement;
      this.classList.add('animate', 'active');
      document.body.classList.add('overflow-hidden');
      this.querySelector('.drawer__inner')?.focus();
      dispatch(this, 'cart-drawer:open');
    }

    close() {
      this.classList.remove('active');
      document.body.classList.remove('overflow-hidden');
      this.activeElement?.focus?.();
      dispatch(this, 'cart-drawer:close');
    }

    getSectionsToRender() {
      return [
        { id: 'cart-drawer', section: 'cart-drawer', selector: '#CartDrawer' },
        { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
      ];
    }

    setActiveElement(element) {
      this.activeElement = element;
    }

    preserveUpsellState(source, target) {
      const currentByIdentity = new Map();
      target.querySelectorAll('cart-drawer-upsell[data-handle]').forEach((upsell) => {
        const identity = upsell.dataset.upsellKey || upsell.dataset.handle;
        if (!identity) return;
        const matches = currentByIdentity.get(identity) || [];
        matches.push(upsell);
        currentByIdentity.set(identity, matches);
      });

      source.querySelectorAll('cart-drawer-upsell[data-handle]').forEach((replacement) => {
        const identity = replacement.dataset.upsellKey || replacement.dataset.handle;
        const matches = currentByIdentity.get(identity);
        const current = matches?.shift();
        if (!current) return;

        const selected = current.dataset.selected === 'true';
        replacement.dataset.selected = String(selected);
        replacement.dataset.selectionHydrated = current.dataset.selectionHydrated || 'true';
        replacement.querySelectorAll('.upsell-toggle-btn').forEach((button) => {
          button.setAttribute('aria-pressed', String(selected));
        });

        if (current.dataset.id) replacement.dataset.id = current.dataset.id;
        const replacementInput = replacement.querySelector('product-form input[name="id"]');
        const currentInput = current.querySelector('product-form input[name="id"]');
        if (replacementInput && currentInput) replacementInput.value = currentInput.value;

        const currentSelects = Array.from(current.querySelectorAll('.upsell__variant-picker select'));
        replacement.querySelectorAll('.upsell__variant-picker select').forEach((select, index) => {
          if (currentSelects[index]) select.value = currentSelects[index].value;
        });
      });
    }

    renderContents(state, options = {}) {
      const drawerHtml = state.sections?.['cart-drawer'];
      const bubbleHtml = state.sections?.['cart-icon-bubble'];
      const parser = new DOMParser();
      if (drawerHtml) {
        const source = parser.parseFromString(drawerHtml, 'text/html').querySelector('#CartDrawer');
        const target = this.querySelector('#CartDrawer');
        if (source && target) {
          this.preserveUpsellState(source, target);
          target.innerHTML = source.innerHTML;
          // Section HTML is authoritative for both add.js and change.js responses.
          // add.js does not necessarily include the total cart item_count.
          const renderedDrawer = source.closest('cart-drawer');
          if (renderedDrawer) {
            this.classList.toggle('is-empty', renderedDrawer.classList.contains('is-empty'));
          } else if (Number.isFinite(state.item_count)) {
            this.classList.toggle('is-empty', state.item_count === 0);
          } else {
            this.classList.toggle('is-empty', Boolean(source.querySelector('.drawer__inner-empty')));
          }
          this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', () => this.close());
          if (this.dataset.upsellBusy === 'true') this.setUpsellBusy(true);
        }
      }
      if (bubbleHtml) {
        const sourceDocument = parser.parseFromString(bubbleHtml, 'text/html');
        const source = sourceDocument.querySelector('#cart-icon-bubble') || sourceDocument.querySelector('.shopify-section');
        const target = document.querySelector('#cart-icon-bubble');
        if (source && target) target.innerHTML = source.innerHTML;
      }
      if (options.open !== false) this.open();
      if (options.reconcile !== false) window.queueMicrotask(() => this.syncPreselectedUpsells());
    }

    getToggleUpsells() {
      return Array.from(this.querySelectorAll('cart-drawer-upsell[data-toggle="true"][data-product-id][data-id]'));
    }

    setUpsellBusy(busy) {
      if (busy) this.dataset.upsellBusy = 'true';
      else delete this.dataset.upsellBusy;
      this.getToggleUpsells().forEach((upsell) => {
        const button = upsell.querySelector('.upsell-toggle-btn');
        if (!button) return;
        button.disabled = busy;
        if (busy) button.setAttribute('aria-disabled', 'true');
        else button.removeAttribute('aria-disabled');
      });
      this.querySelectorAll('[name="checkout"], .shopify-payment-button button, shopify-accelerated-checkout, shopify-accelerated-checkout-cart').forEach((control) => {
        if (busy) {
          control.setAttribute('aria-disabled', 'true');
          control.setAttribute('data-cart-runtime-disabled', 'true');
          if ('disabled' in control) control.disabled = true;
          else control.inert = true;
        } else if (control.dataset.cartRuntimeDisabled === 'true') {
          control.removeAttribute('aria-disabled');
          delete control.dataset.cartRuntimeDisabled;
          if ('disabled' in control) control.disabled = false;
          else control.inert = false;
        }
      });
    }

    showCartError(message = '') {
      const target = this.querySelector('#CartDrawer-CartErrors, .cart-errors, [data-cart-errors]');
      if (!target) return;
      target.textContent = message || window.cartStrings?.error || 'There was an error updating your cart. Please try again.';
      target.hidden = false;
      target.setAttribute('role', 'alert');
    }

    enqueueCartMutation(task) {
      if (typeof task !== 'function') return Promise.resolve({ changed: false, cart: null });
      this.setUpsellBusy(true);
      const previous = this.cartMutationQueue || Promise.resolve();
      const tracked = previous.catch(() => undefined).then(() => task());
      this.cartMutationQueue = tracked;
      this.cartMutationPromise = tracked;
      const clear = () => {
        if (this.cartMutationQueue === tracked) {
          this.cartMutationQueue = null;
          this.cartMutationPromise = null;
          this.setUpsellBusy(false);
        }
      };
      tracked.then(clear, clear);
      return tracked;
    }

    enqueueUpsellMutation(task) {
      return this.enqueueCartMutation(task);
    }

    findUpsell(identity = {}) {
      const key = typeof identity === 'string' ? identity : identity.key;
      const candidates = this.getToggleUpsells();
      if (key) {
        const keyed = candidates.find((upsell) => upsell.dataset.upsellKey === key);
        if (keyed) return keyed;
      }
      return candidates.find((upsell) => (
        upsell.dataset.handle === identity.handle
        && Number(upsell.dataset.productId) === Number(identity.productId)
        && Number(upsell.dataset.id) === Number(identity.variantId)
      )) || null;
    }

    async waitForCartMutations() {
      let pending = this.cartMutationPromise;
      let result = null;
      while (pending) {
        result = await pending;
        if (result?.error) throw result.error;
        if (pending === this.cartMutationPromise) break;
        pending = this.cartMutationPromise;
      }
      return result;
    }

    waitForUpsellMutations() {
      return this.waitForCartMutations();
    }

    findCartUpsellItems(cartItems, identity = {}) {
      const key = typeof identity === 'string' ? identity : identity.key;
      if (!key) return [];
      return cartItems.filter((item) => itemProperty(item, '_theme_upsell_key') === String(key));
    }

    isDecorativeCartItem(item) {
      return Boolean(
        itemProperty(item, '_theme_upsell_key')
        || itemProperty(item, '_theme_gift_key')
        || itemProperty(item, '_theme_additional_key')
      );
    }

    hasPrimaryCartItem(cart) {
      return (cart?.items || []).some((item) => !this.isDecorativeCartItem(item));
    }

    findInvalidAdditionalItem(cart) {
      const items = cart?.items || [];
      if (!this.hasPrimaryCartItem(cart)) return items.find((item) => this.isDecorativeCartItem(item)) || null;
      return items.find((item) => {
        if (!itemProperty(item, '_theme_additional_key')) return false;
        const parentProductId = Number(itemProperty(item, '_theme_parent_product_id'));
        const minimum = Math.max(1, Number(itemProperty(item, '_theme_min_parent_quantity')) || 1);
        if (!parentProductId) return false;
        const parentQuantity = items.reduce((sum, candidate) => {
          if (this.isDecorativeCartItem(candidate)) return sum;
          return Number(candidate.product_id) === parentProductId
            ? sum + Number(candidate.quantity || 0)
            : sum;
        }, 0);
        return parentQuantity < minimum;
      }) || null;
    }

    async removeInvalidAdditionalItems(cart) {
      let state = cart;
      let changed = false;
      let invalid = this.findInvalidAdditionalItem(state);
      let attemptsRemaining = Math.max(2, (state?.items?.length || 0) + 1);
      while (invalid && attemptsRemaining > 0) {
        const response = await postJson(window.routes?.cart_change_url || '/cart/change.js', {
          id: invalid.key,
          quantity: 0,
          sections: ['cart-drawer', 'cart-icon-bubble'],
          sections_url: window.location.pathname,
        });
        const result = await parseJsonResponse(response, 'Unable to remove an ineligible additional item');
        if (!response.ok || result.status || result.errors) {
          throw new Error(result.description || result.message || 'Unable to remove an ineligible additional item');
        }
        if (result.sections) this.renderContents(result, { open: false, reconcile: false });
        state = isFullCart(result) ? result : await this.readCart();
        changed = true;
        attemptsRemaining -= 1;
        invalid = this.findInvalidAdditionalItem(state);
      }
      if (invalid) throw new Error('Additional item eligibility could not be synchronized');
      return { changed, cart: state };
    }

    getUpsellDecisionKey(cart, upsell) {
      const cartToken = String(cart?.token || 'cart').split('?')[0];
      return `theme-upsell:${cartToken}:${upsell.dataset.upsellKey || upsell.dataset.handle || ''}`;
    }

    readUpsellDecision(cart, upsell) {
      try {
        return window.sessionStorage.getItem(this.getUpsellDecisionKey(cart, upsell));
      } catch (_error) {
        return null;
      }
    }

    persistUpsellDecision(cart, upsell, selected) {
      try {
        window.sessionStorage.setItem(this.getUpsellDecisionKey(cart, upsell), String(Boolean(selected)));
      } catch (_error) {}
    }

    hydrateUpsellSelections(cart) {
      const hasPrimaryItem = this.hasPrimaryCartItem(cart);
      this.getToggleUpsells().forEach((upsell) => {
        // Hydrating another/new block must not overwrite a customer's current choice.
        if (hasPrimaryItem && upsell.dataset.selectionHydrated === 'true') return;
        const markedItems = this.findCartUpsellItems(cart?.items || [], { key: upsell.dataset.upsellKey || '' });
        // Pre-marker carts cannot distinguish a main item from an old upsell.
        // Leave unmarked purchases visible and removable as ordinary cart rows.
        const hasUnmarkedProduct = (cart?.items || []).some((item) => (
          !this.isDecorativeCartItem(item)
          && Number(item.product_id) === Number(upsell.dataset.productId)
        ));
        let selected = markedItems.length > 0;
        if (!markedItems.length && hasPrimaryItem) {
          const saved = this.readUpsellDecision(cart, upsell);
          // Removing the visible legacy row must not trigger default re-add.
          if (saved == null && hasUnmarkedProduct) this.persistUpsellDecision(cart, upsell, false);
          selected = saved == null
            ? !hasUnmarkedProduct && upsell.dataset.defaultSelected === 'true'
            : saved === 'true';
        }
        if (!hasPrimaryItem) selected = false;
        upsell.setSelected?.(selected);
        // An empty cart is ineligible, not an opt-out. Apply the saved choice or
        // merchant preselection once a primary item arrives.
        upsell.dataset.selectionHydrated = String(hasPrimaryItem);
      });
    }

    findUpsellMismatch(cart) {
      const cartItems = Array.isArray(cart?.items) ? cart.items : [];
      const hasPrimaryItem = this.hasPrimaryCartItem(cart);
      for (const upsell of this.getToggleUpsells()) {
        const variantId = Number(upsell.dataset.id);
        const identity = {
          key: upsell.dataset.upsellKey || '',
          productId: Number(upsell.dataset.productId),
          variantId,
        };
        const markedItems = this.findCartUpsellItems(cartItems, identity);
        const desiredItem = markedItems.find((item) => Number(item.variant_id || item.id) === variantId);
        const staleItem = markedItems.find((item) => Number(item.variant_id || item.id) !== variantId);
        const selected = hasPrimaryItem && upsell.dataset.selected === 'true';
        if (selected && !desiredItem) return { action: 'add', upsell };
        if (selected && desiredItem && Number(desiredItem.quantity || 0) !== 1) {
          return { action: 'change', upsell, cartItem: desiredItem, quantity: 1 };
        }
        if (selected && staleItem) return { action: 'remove', upsell, cartItem: staleItem };
        if (!selected && markedItems[0]) return { action: 'remove', upsell, cartItem: markedItems[0] };
      }
      return null;
    }

    async applyUpsellMismatch(mismatch) {
      const sections = ['cart-drawer', 'cart-icon-bubble'];
      const payload = mismatch.action === 'add'
        ? {
            items: [{
              id: Number(mismatch.upsell.dataset.id),
              quantity: 1,
              properties: { _theme_upsell_key: mismatch.upsell.dataset.upsellKey || '' },
            }],
            sections,
            sections_url: window.location.pathname,
          }
        : {
            id: mismatch.cartItem?.key,
            quantity: mismatch.action === 'change' ? Number(mismatch.quantity || 1) : 0,
            sections,
            sections_url: window.location.pathname,
          };
      if (mismatch.action !== 'add' && !payload.id) {
        throw new Error('Unable to identify the selected cart upsell');
      }
      const route = mismatch.action === 'add'
        ? (window.routes?.cart_add_url || '/cart/add.js')
        : (window.routes?.cart_change_url || '/cart/change.js');
      const response = await postJson(route, payload);
      const state = await parseJsonResponse(response, 'Unable to synchronize cart upsell');
      if (!response.ok || state.status) {
        throw new Error(state.description || state.message || 'Unable to synchronize cart upsell');
      }
      if (state.sections) this.renderContents(state, { open: false, reconcile: false });
      return mismatch.action !== 'add' && isFullCart(state) ? state : this.readCart();
    }

    async readCart() {
      const cartRoute = window.routes?.cart_url || '/cart';
      const response = await fetch(`${cartRoute.replace(/\/$/, '')}.js`, {
        headers: { Accept: 'application/json' },
      });
      const cart = await parseJsonResponse(response, 'Unable to verify cart');
      if (!response.ok || cart.status) throw new Error(cart.description || cart.message || 'Unable to verify cart');
      return cart;
    }

    async reconcileUpsells() {
      let cart = await this.readCart();
      const additionalResult = await this.removeInvalidAdditionalItems(cart);
      cart = additionalResult.cart;
      if (!this.hasPrimaryCartItem(cart)
        || this.getToggleUpsells().some((upsell) => upsell.dataset.selectionHydrated !== 'true')) {
        this.hydrateUpsellSelections(cart);
      }
      let changed = additionalResult.changed;
      let attemptsRemaining = Math.max(1, this.getToggleUpsells().length * 4 + 1);
      let mismatch = this.findUpsellMismatch(cart);

      while (mismatch && attemptsRemaining > 0) {
        cart = await this.applyUpsellMismatch(mismatch);
        changed = true;
        attemptsRemaining -= 1;
        mismatch = this.findUpsellMismatch(cart);
      }

      if (mismatch) throw new Error('Cart upsell state could not be synchronized');
      if (changed) {
        if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-drawer-upsells',
            cartData: cart,
          });
        }
        dispatch(this, 'cart:updated', { cart, source: 'cart-drawer-upsells' });
      }
      return { changed, cart };
    }

    syncPreselectedUpsells() {
      if (this.upsellSyncPromise) return this.upsellSyncPromise;

      const syncTask = async () => {
        try {
          return await this.reconcileUpsells();
        } catch (error) {
          console.error(error);
          dispatch(this, 'cart:error', { message: error.message });
          return { changed: false, cart: null, error };
        }
      };
      this.upsellSyncPromise = this.enqueueCartMutation(syncTask);
      const clearSync = () => {
        this.upsellSyncPromise = null;
      };
      this.upsellSyncPromise.then(clearSync, clearSync);
      return this.upsellSyncPromise;
    }

    async onCheckoutSubmit(event) {
      const form = event.target.closest('form');
      const checkoutButton = event.submitter?.name === 'checkout'
        ? event.submitter
        : Array.from(this.querySelectorAll('[name="checkout"]')).find((button) => button.form === form);
      if (!form || !checkoutButton || this.dataset.upsellCheckoutCommit === 'true') return;

      event.preventDefault();
      if (this.dataset.upsellCheckoutRunning === 'true') return;
      this.dataset.upsellCheckoutRunning = 'true';

      try {
        await this.waitForCartMutations();
        for (const gift of this.querySelectorAll('cart-drawer-gift')) {
          await gift.update?.();
        }
        await this.waitForCartMutations();
        const syncResult = await this.syncPreselectedUpsells();
        if (syncResult?.error) throw syncResult.error;
        await this.waitForCartMutations();
        this.dataset.upsellCheckoutCommit = 'true';
        const checkoutInput = document.createElement('input');
        checkoutInput.type = 'hidden';
        checkoutInput.name = 'checkout';
        checkoutInput.value = checkoutButton.value || 'Checkout';
        checkoutInput.dataset.upsellCheckoutCommit = 'true';
        form.append(checkoutInput);
        HTMLFormElement.prototype.submit.call(form);
      } catch (error) {
        delete this.dataset.upsellCheckoutRunning;
        dispatch(this, 'cart:error', { message: error.message });
      }
    }

    disconnectedCallback() {
      document.removeEventListener('cart:updated', this.onCartUpdated);
      this.removeEventListener('cart:error', this.onCartError);
    }
  }
  define('cart-drawer', CartDrawer);

  class CartDrawerUpsells extends HTMLElement {
    connectedCallback() {
      if (this.dataset.runtimeBound) return;
      this.dataset.runtimeBound = 'true';
      window.queueMicrotask(() => {
        this.placeInConfiguredRegion();
      });
    }

    placeInConfiguredRegion() {
      const drawer = this.closest('cart-drawer') || document.querySelector('cart-drawer');
      if (!drawer) return;
      const selector = this.dataset.position === 'footer' ? '.drawer__footer' : '.cart-drawer__body';
      const target = drawer.querySelector(selector);
      if (target && !target.contains(this)) target.append(this);
    }

  }
  define('cart-drawer-upsells', CartDrawerUpsells);

  class CartDrawerUpsell extends HTMLElement {
    connectedCallback() {
      if (this.dataset.runtimeBound) return;
      this.dataset.runtimeBound = 'true';
      this.variants = parseJsonScript(this);
      this.toggleButton = this.querySelector('.upsell-toggle-btn');
      this.variantInput = this.querySelector('product-form input[name="id"]');

      this.querySelectorAll('.upsell__variant-picker select').forEach((select) => {
        select.addEventListener('change', () => this.updateVariant({ reconcileCart: true }));
      });
      this.toggleButton?.addEventListener('click', (event) => {
        if (event.target.closest('select, option, input, a, product-form')) return;
        event.preventDefault();
        const drawer = this.closest('cart-drawer') || document.querySelector('cart-drawer');
        const identity = this.getIdentity();
        const desiredSelected = this.dataset.selected !== 'true';
        drawer?.enqueueUpsellMutation(() => {
          const current = drawer.findUpsell(identity) || this;
          return current.toggle(desiredSelected);
        });
      });

      this.setSelected(this.dataset.selected === 'true');
      this.updateVariant();
    }

    updateVariant(options = {}) {
      if (!this.variants.length) return;
      const picker = this.querySelector('.upsell__variant-picker') || this;
      const variant = resolveSelectableVariant(picker, this.variants, {
        skipUnavailable: this.dataset.skipUnavailable === 'true',
        skipNonExistent: this.dataset.skipNonExistent === 'true',
      });
      if (!variant || !variant.available) {
        this.dataset.variantAvailable = 'false';
        this.setSelected(false);
        this.toggleButton?.setAttribute('aria-disabled', 'true');
        if (this.toggleButton?.tagName === 'BUTTON') this.toggleButton.disabled = true;
        if (options.reconcileCart) {
          const drawer = this.closest('cart-drawer') || document.querySelector('cart-drawer');
          drawer?.syncPreselectedUpsells();
        }
        return;
      }
      const previousVariantId = Number(this.dataset.id);
      this.dataset.id = String(variant.id);
      this.dataset.variantAvailable = 'true';
      this.toggleButton?.removeAttribute('aria-disabled');
      if (this.toggleButton?.tagName === 'BUTTON') this.toggleButton.disabled = false;
      if (this.variantInput) this.variantInput.value = String(variant.id);

      if (this.dataset.updatePrices === 'true') {
        const price = Number(variant.price || 0);
        const comparePrice = Number(variant.compare_at_price) > Number(variant.price)
          ? Number(variant.compare_at_price)
          : Number(variant.price || 0);
        this.querySelector('.regular-price')?.replaceChildren(formatMoney(price, this.dataset.moneyFormat));
        const compare = this.querySelector('.compare-price');
        if (compare) {
          compare.replaceChildren(formatMoney(comparePrice, this.dataset.moneyFormat));
          compare.classList.toggle('hidden', comparePrice <= price);
        }
      }

      if (
        options.reconcileCart
        && this.dataset.selected === 'true'
        && previousVariantId
        && previousVariantId !== Number(variant.id)
      ) {
        const drawer = this.closest('cart-drawer') || document.querySelector('cart-drawer');
        drawer?.syncPreselectedUpsells();
      }
    }

    setSelected(selected) {
      this.dataset.selected = String(selected);
      this.toggleButton?.setAttribute('aria-pressed', String(selected));
    }

    getIdentity() {
      return {
        key: this.dataset.upsellKey || '',
        handle: this.dataset.handle || '',
        productId: Number(this.dataset.productId),
        variantId: Number(this.dataset.id),
      };
    }

    async toggle(desiredSelected = this.dataset.selected !== 'true') {
      if (this.dataset.loading === 'true') return { changed: false, cart: null };
      if (this.dataset.variantAvailable === 'false') return { changed: false, cart: null };
      this.dataset.loading = 'true';
      const wasSelected = this.dataset.selected === 'true';
      const drawer = this.closest('cart-drawer') || document.querySelector('cart-drawer');
      const identity = this.getIdentity();
      const wasHydrated = this.dataset.selectionHydrated === 'true';
      // Explicit input takes priority even if initial hydration is still pending.
      this.dataset.selectionHydrated = 'true';
      this.setSelected(desiredSelected);

      try {
        if (!drawer) throw new Error('Cart drawer is unavailable');
        const result = await drawer.reconcileUpsells();
        const verifiedCart = result.cart || await drawer.readCart();
        const verifiedSelected = drawer.findCartUpsellItems(verifiedCart.items || [], identity).some((item) => (
          Number(item.variant_id || item.id) === identity.variantId
        ));
        if (verifiedSelected !== desiredSelected) throw new Error('Cart upsell state could not be verified');

        const current = drawer.findUpsell(identity);
        current?.setSelected(desiredSelected);
        if (current) drawer.persistUpsellDecision(verifiedCart, current, desiredSelected);
        if (verifiedCart.item_count !== undefined) drawer.classList.toggle('is-empty', verifiedCart.item_count === 0);
        // reconcileUpsells already published the verified cart once.
        return { changed: result.changed, cart: verifiedCart };
      } catch (error) {
        const current = drawer?.findUpsell(identity) || this;
        current.setSelected(wasSelected);
        current.dataset.selectionHydrated = String(wasHydrated);
        console.error(error);
        dispatch(current, 'cart:error', { message: error.message });
        return { changed: false, error };
      } finally {
        const current = drawer?.findUpsell(identity) || this;
        current.dataset.loading = 'false';
      }
    }
  }
  define('cart-drawer-upsell', CartDrawerUpsell);

  class ProductInfoUpsell extends HTMLElement {
    connectedCallback() {
      if (this.dataset.runtimeBound) return;
      this.dataset.runtimeBound = 'true';
      this.variants = parseJsonScript(this);
      this.toggleButton = this.querySelector('.upsell-toggle-btn');
      this.variantInput = this.querySelector('product-form input[name="id"]');

      this.querySelectorAll('.upsell__variant-picker select').forEach((select) => {
        select.addEventListener('change', () => this.updateVariant());
      });

      if (this.dataset.toggle === 'true' && this.toggleButton) {
        if (this.toggleButton.tagName !== 'BUTTON') {
          this.toggleButton.setAttribute('role', 'button');
          this.toggleButton.setAttribute('tabindex', '0');
          this.toggleButton.addEventListener('keydown', (event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            this.toggle();
          });
        }
        this.toggleButton.addEventListener('click', (event) => {
          if (event.target.closest('select, option, input, a, product-form')) return;
          event.preventDefault();
          this.toggle();
        });
      }

      this.setSelected(this.dataset.selected === 'true', false);
      this.updateVariant();
    }

    updateVariant() {
      if (!this.variants.length) return;
      const picker = this.querySelector('.upsell__variant-picker') || this;
      const variant = resolveSelectableVariant(picker, this.variants, {
        skipUnavailable: this.dataset.skipUnavailable === 'true',
        skipNonExistent: this.dataset.skipNonExistent === 'true',
      });
      if (!variant || !variant.available) {
        this.dataset.variantAvailable = 'false';
        this.setSelected(false);
        this.toggleButton?.setAttribute('aria-disabled', 'true');
        if (this.toggleButton?.tagName === 'BUTTON') this.toggleButton.disabled = true;
        return;
      }

      this.dataset.id = String(variant.id);
      this.dataset.variantAvailable = 'true';
      this.toggleButton?.removeAttribute('aria-disabled');
      if (this.toggleButton?.tagName === 'BUTTON') this.toggleButton.disabled = false;
      if (this.variantInput) this.variantInput.value = String(variant.id);

      if (this.dataset.updatePrices === 'true') {
        const price = Number(variant.price || 0);
        const comparePrice = Number(variant.compare_at_price) > Number(variant.price)
          ? Number(variant.compare_at_price)
          : Number(variant.price || 0);
        this.querySelector('.regular-price')?.replaceChildren(formatMoney(price, this.dataset.moneyFormat));
        const compare = this.querySelector('.compare-price');
        if (compare) {
          compare.replaceChildren(formatMoney(comparePrice, this.dataset.moneyFormat));
          compare.classList.toggle('hidden', comparePrice <= price);
        }
      }
    }

    setSelected(selected, announce = true) {
      this.dataset.selected = String(selected);
      this.classList.toggle('upsell--selected', selected);
      this.toggleButton?.setAttribute('aria-pressed', String(selected));
      if (announce) dispatch(this, 'upsell:change', { selected, variantId: this.dataset.id });
    }

    toggle() {
      if (this.dataset.variantAvailable === 'false') return;
      this.setSelected(this.dataset.selected !== 'true');
    }
  }
  define('product-info-upsell', ProductInfoUpsell);

  class ComparisonSlider extends HTMLElement {
    connectedCallback() {
      const range = this.querySelector('input[type="range"]');
      if (!range || range.dataset.runtimeBound) return;
      range.dataset.runtimeBound = 'true';
      const update = () => this.style.setProperty('--position', `${range.value}%`);
      range.addEventListener('input', update);
      update();
    }
  }
  define('comparison-slider', ComparisonSlider);

  class InternalVideo extends HTMLElement {
    connectedCallback() {
      if (this.dataset.runtimeBound) return;
      this.dataset.runtimeBound = 'true';
      this.video = this.querySelector('video');
      if (!this.video) return;
      this.timeline = this.querySelector('.internal-video__timeline');
      this.querySelectorAll('[data-play], .internal-video__play, .play-btn').forEach((button) => button.addEventListener('click', () => this.toggle()));
      this.querySelectorAll('[data-sound], .internal-video__sound, .internal-video__sound-btn').forEach((button) => button.addEventListener('click', () => this.toggleSound()));
      this.querySelectorAll('[data-fullscreen], .internal-video__fullscreen-btn').forEach((button) => button.addEventListener('click', () => this.toggleFullscreen()));
      this.video.addEventListener('play', () => this.updatePlaybackState());
      this.video.addEventListener('pause', () => this.updatePlaybackState());
      this.video.addEventListener('ended', () => this.updatePlaybackState());
      this.video.addEventListener('waiting', () => this.classList.add('internal-video--loading'));
      this.video.addEventListener('playing', () => this.classList.remove('internal-video--loading'));
      this.video.addEventListener('timeupdate', () => this.updateTimeline());
      this.onFullscreenChange = () => this.classList.toggle('internal-video--fullscreen', document.fullscreenElement === this);
      document.addEventListener('fullscreenchange', this.onFullscreenChange);
      if (this.timeline) {
        this.timeline.setAttribute('role', 'slider');
        this.timeline.setAttribute('aria-valuemin', '0');
        this.timeline.setAttribute('aria-valuemax', '100');
        if (this.timeline.tabIndex < 0) this.timeline.tabIndex = 0;
        this.timeline.addEventListener('pointerdown', (event) => this.startTimelineDrag(event));
        this.timeline.addEventListener('keydown', (event) => this.onTimelineKeydown(event));
      }
      this.updatePlaybackState();
      this.updateSoundState();
      this.updateTimeline();
      if (this.dataset.autoplay === 'true') this.observe();
    }

    toggle() {
      if (this.video.paused) this.video.play().catch(() => {});
      else this.video.pause();
    }

    updatePlaybackState() {
      const playing = !this.video.paused && !this.video.ended;
      this.dataset.playing = String(playing);
      this.classList.toggle('internal-video--playing', playing);
    }

    toggleSound() {
      this.video.muted = !this.video.muted;
      this.updateSoundState();
    }

    updateSoundState() {
      const muted = this.video.muted;
      this.dataset.muted = String(muted);
      this.classList.toggle('internal-video--muted', muted);
      this.querySelectorAll('[data-sound], .internal-video__sound, .internal-video__sound-btn').forEach((button) => {
        button.setAttribute('aria-pressed', String(!muted));
      });
    }

    toggleFullscreen() {
      if (document.fullscreenElement === this) document.exitFullscreen?.();
      else this.requestFullscreen?.().catch?.(() => {});
    }

    updateTimeline() {
      if (!this.timeline) return;
      const percent = this.video.duration > 0 ? (this.video.currentTime / this.video.duration) * 100 : 0;
      this.timeline.style.setProperty('--completed', `${percent}%`);
      this.timeline.setAttribute('aria-valuenow', String(Math.round(percent)));
    }

    seekFromPointer(event) {
      if (!this.timeline || !(this.video.duration > 0)) return;
      const rect = this.timeline.getBoundingClientRect();
      const ratio = Math.max(0, Math.min(1, (event.clientX - rect.left) / Math.max(1, rect.width)));
      this.video.currentTime = ratio * this.video.duration;
      this.updateTimeline();
    }

    startTimelineDrag(event) {
      event.preventDefault();
      this.timeline.setPointerCapture?.(event.pointerId);
      this.seekFromPointer(event);
      const move = (moveEvent) => this.seekFromPointer(moveEvent);
      const stop = () => {
        this.timeline.removeEventListener('pointermove', move);
        this.timeline.removeEventListener('pointerup', stop);
        this.timeline.removeEventListener('pointercancel', stop);
      };
      this.timeline.addEventListener('pointermove', move);
      this.timeline.addEventListener('pointerup', stop);
      this.timeline.addEventListener('pointercancel', stop);
    }

    onTimelineKeydown(event) {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key) || !(this.video.duration > 0)) return;
      event.preventDefault();
      if (event.key === 'Home') this.video.currentTime = 0;
      else if (event.key === 'End') this.video.currentTime = this.video.duration;
      else this.video.currentTime = Math.max(0, Math.min(this.video.duration, this.video.currentTime + (event.key === 'ArrowRight' ? 5 : -5)));
      this.updateTimeline();
    }

    observe() {
      this.observer = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) this.video.play().catch(() => {});
        else this.video.pause();
      }, { threshold: 0.35 });
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      document.removeEventListener('fullscreenchange', this.onFullscreenChange);
    }
  }
  define('internal-video', InternalVideo);

  class DynamicDates extends HTMLElement {
    connectedCallback() {
      const days = (this.dataset.dayLabels || '').split(',').map((value) => value.trim()).filter(Boolean);
      const months = (this.dataset.monthLabels || '').split(',').map((value) => value.trim()).filter(Boolean);
      this.skippedDays = this.parseSkippedDays(this.dataset.daysToSkip || '');
      this.skippedDates = this.parseSkippedDates(this.dataset.datesToSkip || '');
      this.querySelectorAll('[data-dynamic-date="true"]').forEach((element) => {
        const min = Number(element.dataset.minDays || 0);
        const max = Number(element.dataset.maxDays || min);
        const template = element.dataset.text || element.textContent;
        element.innerHTML = template
          .replace(/\[start_date\]/g, this.format(this.addDaysSkipping(min), days, months))
          .replace(/\[end_date\]/g, this.format(this.addDaysSkipping(max), days, months));
      });
    }

    parseSkippedDays(value) {
      const aliases = {
        sun: 0, sunday: 0, '周日': 0, '星期日': 0, '星期天': 0,
        mon: 1, monday: 1, '周一': 1, '星期一': 1,
        tue: 2, tues: 2, tuesday: 2, '周二': 2, '星期二': 2,
        wed: 3, wednesday: 3, '周三': 3, '星期三': 3,
        thu: 4, thur: 4, thurs: 4, thursday: 4, '周四': 4, '星期四': 4,
        fri: 5, friday: 5, '周五': 5, '星期五': 5,
        sat: 6, saturday: 6, '周六': 6, '星期六': 6,
      };
      return new Set(String(value).split(',').map((part) => {
        const normalized = part.trim().toLocaleLowerCase();
        if (/^[0-6]$/.test(normalized)) return Number(normalized);
        return aliases[normalized];
      }).filter((day) => day !== undefined));
    }

    parseSkippedDates(value) {
      return String(value).split(',').map((part) => part.trim()).filter(Boolean).map((part) => {
        const match = part.match(/^(?:(\d{4})[-/.])?(\d{1,2})[-/.](\d{1,2})$/);
        return match ? { year: Number(match[1]) || null, month: Number(match[2]), day: Number(match[3]) } : null;
      }).filter(Boolean);
    }

    isSkipped(date) {
      if (this.skippedDays.has(date.getDay())) return true;
      return this.skippedDates.some((item) => (
        (!item.year || item.year === date.getFullYear())
        && item.month === date.getMonth() + 1
        && item.day === date.getDate()
      ));
    }

    addDaysSkipping(amount) {
      const value = new Date();
      value.setHours(12, 0, 0, 0);
      let remaining = Math.max(0, Number(amount) || 0);
      while (this.isSkipped(value) || remaining > 0) {
        value.setDate(value.getDate() + 1);
        if (!this.isSkipped(value) && remaining > 0) remaining -= 1;
      }
      return value;
    }

    format(date, days, months) {
      const format = this.dataset.dateFormat || 'month_day';
      const month = months[date.getMonth()] || date.toLocaleString(document.documentElement.lang || 'en', { month: 'short' });
      const day = days[date.getDay()] || date.toLocaleString(document.documentElement.lang || 'en', { weekday: 'short' });
      if (format.includes('day_month')) return `${day}, ${date.getDate()} ${month}`;
      if (format.includes('numeric')) return date.toLocaleDateString(document.documentElement.lang || 'en');
      return `${day}, ${month} ${date.getDate()}`;
    }
  }
  define('dynamic-dates', DynamicDates);

  class ClickableDiscount extends HTMLElement {
    connectedCallback() {
      const button = this.querySelector('.clickable-discount__btn');
      if (!button || button.dataset.runtimeBound) return;
      button.dataset.runtimeBound = 'true';
      button.addEventListener('click', async () => {
        this.dataset.loading = 'true';
        try {
          const code = String(this.dataset.code || '').trim();
          if (!code) throw new Error('Discount code is empty');
          const response = await postJson(window.routes?.cart_update_url || '/cart/update.js', { discount: code });
          const cart = await parseJsonResponse(response, 'Discount could not be applied');
          const applied = (cart.discount_codes || []).some((discount) => (
            String(discount.code || '').toLocaleLowerCase() === code.toLocaleLowerCase()
            && discount.applicable !== false
          ));
          if (!response.ok || cart.status || !applied) throw new Error(cart.description || cart.message || 'Discount could not be applied');
          this.dataset.applied = 'true';
          this.dataset.error = 'false';
          button.setAttribute('aria-pressed', 'true');
          dispatch(this, 'cart:updated', { cart, source: 'clickable-discount' });
        } catch (_error) {
          this.dataset.error = 'true';
          this.dataset.applied = 'false';
        } finally {
          this.dataset.loading = 'false';
        }
      });
    }
  }
  define('clickable-discount', ClickableDiscount);

  class CustomProductField extends HTMLElement {
    connectedCallback() {
      if (this.dataset.runtimeBound) return;
      this.dataset.runtimeBound = 'true';
      const productInfo = this.closest('product-info');
      const section = this.dataset.section || productInfo?.dataset.section || '';
      const escapedSection = window.CSS?.escape ? CSS.escape(String(section)) : String(section);
      const form = productInfo?.querySelector(`#product-form-${escapedSection}`)
        || productInfo?.querySelector('form[data-type="add-to-cart-form"]')
        || this.closest('form');
      const controls = Array.from(this.querySelectorAll('input:not([type="hidden"]), textarea, select'));
      const control = controls[0];
      if (!form || !control || !this.dataset.name) return;
      if (!form.id) form.id = `product-form-${section || Math.random().toString(36).slice(2)}`;
      const propertyName = `properties[${this.dataset.name}]`;
      controls.forEach((item) => {
        item.name = propertyName;
        item.setAttribute('form', form.id);
      });
      const required = this.dataset.required === 'true' || control.required;
      controls.forEach((item) => { item.required = required; });
      this.validate = (report = false) => {
        const radios = this.querySelectorAll('input[type="radio"]');
        const checked = this.querySelector('input[type="radio"]:checked');
        const value = radios.length ? (checked?.value || '') : (control.value || '');
        controls.forEach((item) => item.setCustomValidity(''));
        const missingRequired = required && !String(value).trim();
        const message = missingRequired
          ? (this.dataset.atcErrorMsg || this.querySelector('.input-error-msg')?.textContent?.trim() || 'Please complete this field.')
          : '';
        control.setCustomValidity(message);
        const invalid = missingRequired || !control.checkValidity();
        control.setAttribute('aria-invalid', String(invalid));
        this.classList.toggle('field-error', invalid);
        if (report && invalid) {
          control.reportValidity();
          control.focus?.();
        }
        return !invalid;
      };
      this.addEventListener('change', () => this.validate(false));
      this.addEventListener('input', () => this.validate(false));
      this.validate(false);
    }
  }
  define('custom-product-field', CustomProductField);

  class CardVariantSelects extends HTMLElement {
    connectedCallback() {
      if (this.dataset.runtimeBound) return;
      this.dataset.runtimeBound = 'true';
      this.variants = parseJsonScript(this);
      this.addEventListener('change', () => this.update());
      this.update();
    }

    update() {
      const values = Array.from(this.querySelectorAll('select, fieldset')).map((group) =>
        group.matches('select') ? group.value : group.querySelector('input:checked')?.value
      ).filter((value) => value !== undefined);
      const variant = findVariant(this.variants, values);
      const formId = this.getAttribute('if') || this.dataset.section;
      const form = document.getElementById(formId) || document.getElementById(`${formId}-submit`)?.form;
      const idInput = form?.querySelector('input[name="id"]');
      if (idInput && variant) idInput.value = variant.id;
      const card = this.closest('.card-wrapper, .card');
      const button = card?.querySelector('[type="submit"]');
      if (button) button.disabled = !variant?.available;
      const image = card?.querySelector('.card__media img, .media img');
      const source = variant?.featured_media?.preview_image?.src || variant?.featured_image?.src;
      if (image && source) image.src = source;
      dispatch(this, 'variant:change', { variant });
    }
  }
  define('card-variant-selects', CardVariantSelects);

  class QuantityBreakUpsells extends HTMLElement {
    connectedCallback() {
      if (this.dataset.runtimeBound) return;
      this.dataset.runtimeBound = 'true';
      this.querySelectorAll('[data-toggle="true"]').forEach((item) => item.addEventListener('click', (event) => {
        if (event.target.closest('select, a, button')) return;
        item.dataset.selected = String(item.dataset.selected !== 'true');
        dispatch(this, 'upsell:change', { item, selected: item.dataset.selected === 'true' });
      }));
    }
  }
  define('quantity-break-upsells', QuantityBreakUpsells);

  class QuantityBreaks extends HTMLElement {
    connectedCallback() {
      if (this.dataset.runtimeBound) return;
      this.dataset.runtimeBound = 'true';
      this.addEventListener('change', (event) => {
        const quantity = event.target.closest('input[name="quantity"]');
        if (quantity) this.applyQuantity(quantity);
        const select = event.target.closest('select');
        if (select) this.updateOfferVariant(select.closest('[data-index], .quantity-break'));
      });
      const selected = this.querySelector('input[name="quantity"]:checked');
      if (selected) this.applyQuantity(selected);
    }

    applyQuantity(input) {
      const amount = Number(input.dataset.quantity || input.value || 1);
      const productInfo = this.closest('product-info');
      const escapedSection = window.CSS?.escape
        ? CSS.escape(String(this.dataset.section || ''))
        : String(this.dataset.section || '');
      const field = productInfo?.querySelector(
        `#Quantity-${escapedSection}, #quantity-${escapedSection}, .quantity__input[name="quantity"]`
      );
      if (field && !this.contains(field) && Number(field.value) !== amount) {
        field.value = amount;
        field.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this.querySelectorAll('quantity-break-upsells').forEach((upsell) => {
        upsell.hidden = Number(upsell.dataset.offer) !== Number(input.dataset.index || input.value || 1);
      });
      dispatch(this, 'quantity-break:change', { quantity: amount, input, section: this.dataset.section || '' });
    }

    updateOfferVariant(container) {
      if (!container) return;
      const variants = parseJsonScript(container);
      const variant = findVariant(variants, selectedValues(container));
      if (!variant) return;
      container.dataset.selectedId = variant.id;
      container.querySelectorAll('[data-product], input[name="id"]').forEach((element) => {
        if (element.matches('input')) element.value = variant.id;
        else element.dataset.product = variant.id;
      });
    }
  }
  define('quantity-breaks', QuantityBreaks);

  class QuantityGifts extends HTMLElement {
    connectedCallback() {
      if (this.dataset.runtimeBound) return;
      this.dataset.runtimeBound = 'true';
      const escapedSection = window.CSS?.escape
        ? CSS.escape(String(this.dataset.section || ''))
        : String(this.dataset.section || '');
      const productInfo = this.closest('product-info');
      this.quantityInput = productInfo?.querySelector(
        `#Quantity-${escapedSection}, #quantity-${escapedSection}, .quantity__input[name="quantity"]`
      );
      this.quantityInput?.addEventListener('change', () => this.update());
      this.onQuantityBreakChange = (event) => {
        if (String(event.detail?.section || '') !== String(this.dataset.section || '')) return;
        this.update(event.detail.quantity);
      };
      document.addEventListener('quantity-break:change', this.onQuantityBreakChange);
      this.update();
    }

    update(quantity = Number(this.quantityInput?.value || 1)) {
      this.querySelectorAll('.quantity-gift').forEach((gift) => {
        const unlocked = quantity >= Number(gift.dataset.quantity || 1);
        gift.dataset.unlocked = String(unlocked);
        gift.classList.toggle('quantity-gift--unlocked', unlocked);
        const form = gift.querySelector('product-form, form');
        if (form) form.dataset.selected = String(unlocked);
      });
      dispatch(this, 'quantity-gifts:change', { quantity });
    }

    disconnectedCallback() {
      document.removeEventListener('quantity-break:change', this.onQuantityBreakChange);
    }
  }
  define('quantity-gifts', QuantityGifts);

  class SubscriptionWidget extends HTMLElement {
    connectedCallback() {
      if (this.dataset.runtimeBound) return;
      this.dataset.runtimeBound = 'true';
      const sectionSelector = window.CSS?.escape ? CSS.escape(String(this.dataset.section || '')) : String(this.dataset.section || '');
      this.form = this.closest('product-info')?.querySelector(`#product-form-${sectionSelector}`)
        || document.getElementById(`product-form-${this.dataset.section}`)
        || this.closest('studio-product-form, product-form')?.querySelector('form');
      this.hiddenInput = this.form?.querySelector('input[name="selling_plan"]');
      if (this.form && !this.hiddenInput) {
        this.hiddenInput = document.createElement('input');
        this.hiddenInput.type = 'hidden';
        this.hiddenInput.name = 'selling_plan';
        this.form.append(this.hiddenInput);
      }
      this.addEventListener('click', (event) => {
        const box = event.target.closest('.subscription__box');
        if (box) this.selectMode(box);
      });
      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-selling-plan-id], #SubscriptionFrequency-' + this.dataset.section)) this.updatePlan(event.target);
      });
      const selected = this.querySelector('.subscription__box[data-selected="true"]') || this.querySelector('.subscription__box');
      if (selected) this.selectMode(selected);
    }

    selectMode(box) {
      this.querySelectorAll('.subscription__box').forEach((item) => {
        const active = item === box;
        item.dataset.selected = String(active);
        item.classList.toggle('subscription__box--selected', active);
      });
      const subscription = box.dataset.type === 'subscription';
      const control = box.querySelector('select, input[data-selling-plan-id]:checked') || this.querySelector('[data-selling-plan-id]');
      const selector = control?.matches('select') ? control.selectedOptions[0] : control;
      const sellingPlanId = subscription ? (selector?.dataset.sellingPlanId || selector?.value || this.dataset.firstSellingPlanId || '') : '';
      if (this.hiddenInput) {
        this.hiddenInput.value = sellingPlanId;
        this.hiddenInput.disabled = !subscription;
      }
      this.updatePrices(subscription ? selector : null);
      dispatch(this, 'selling-plan:change', { sellingPlanId, subscription });
    }

    updatePlan(control) {
      const selected = control.matches('select') ? control.selectedOptions[0] : control;
      if (this.hiddenInput && !this.hiddenInput.disabled) this.hiddenInput.value = selected?.dataset.sellingPlanId || selected?.value || '';
      this.updatePrices(selected);
      dispatch(this, 'selling-plan:change', { sellingPlanId: this.hiddenInput?.value || '', subscription: !this.hiddenInput?.disabled });
    }

    updatePrices(planControl) {
      const subscription = Boolean(planControl && !this.hiddenInput?.disabled);
      const price = subscription ? Number(planControl.dataset.price || 0) : Number(this.dataset.basePrice || 0);
      const comparePrice = subscription
        ? Number(planControl.dataset.comparePrice || price)
        : Number(this.dataset.baseComparePrice || price);
      const amountSaved = Math.max(0, comparePrice - price);
      const amountSavedRounded = Math.round(amountSaved / 100) * 100;
      const percentageSaved = comparePrice > 0 ? Math.floor((amountSaved * 100) / comparePrice) : 0;
      const selector = subscription ? '.sub-price-update' : '.one-time-price-update';
      this.querySelectorAll(selector).forEach((node) => {
        const template = node.dataset.text;
        if (!template) return;
        node.innerHTML = template
          .replaceAll('[subscription_name]', this.dataset.subscriptionName || '')
          .replaceAll('[price]', formatMoney(price, this.dataset.moneyFormat))
          .replaceAll('[compare_price]', formatMoney(comparePrice, this.dataset.moneyFormat))
          .replaceAll('[amount_saved]', formatMoney(amountSaved, this.dataset.moneyFormat))
          .replaceAll('[amount_saved_rounded]', formatMoney(amountSavedRounded, this.dataset.moneyFormat))
          .replaceAll('[percentage_saved]', `${percentageSaved}%`);
      });
      dispatch(this, 'subscription-price:change', {
        price,
        comparePrice,
        amountSaved,
        percentageSaved,
        subscription,
        sellingPlanId: this.hiddenInput?.disabled ? '' : this.hiddenInput?.value || '',
      });
    }
  }
  define('theme-subscription-widget', SubscriptionWidget);

  class PromoPopup extends HTMLElement {
    connectedCallback() {
      if (this.dataset.runtimeBound) return;
      this.dataset.runtimeBound = 'true';
      this.overlay = this.querySelector('.sign-up-popup-overlay');
      this.modal = this.querySelector('.sign-up-popup-modal');
      if (!this.overlay || !this.modal) return;

      this.storageKey = `theme-promo-popup-dismissed-${window.location.host}`;
      this.closeHandler = () => this.close(true);
      this.querySelectorAll('.promp-popup__close-btn').forEach((button) => button.addEventListener('click', this.closeHandler));

      const delayDays = Math.max(0, Number(this.dataset.delayDays || 0));
      const dismissedAt = Number(window.localStorage?.getItem(this.storageKey) || 0);
      const suppressed = !this.isTestMode && dismissedAt > 0 && Date.now() - dismissedAt < delayDays * 86400000;
      if (suppressed) return;

      const delayMs = this.isTestMode ? 0 : Math.max(0, Number(this.dataset.delaySeconds || 0)) * 1000;
      this.openTimer = window.setTimeout(() => this.open(), delayMs);
    }

    get isTestMode() {
      return this.dataset.testMode === 'true';
    }

    open() {
      this.overlay?.classList.add('popup-overlay--active');
      this.modal?.classList.add('popup-modal--active');
      this.modal?.setAttribute('role', 'dialog');
      this.modal?.setAttribute('aria-modal', 'true');
      this.modal?.querySelector('input, button, a[href]')?.focus({ preventScroll: true });
      if (this.dataset.displayTimer === 'true') this.startCountdown();
    }

    close(remember = false) {
      this.overlay?.classList.remove('popup-overlay--active');
      this.modal?.classList.remove('popup-modal--active');
      if (remember && !this.isTestMode) window.localStorage?.setItem(this.storageKey, String(Date.now()));
      window.clearInterval(this.countdownTimer);
    }

    startCountdown() {
      window.clearInterval(this.countdownTimer);
      this.remainingSeconds = Math.max(0, Math.round(Number(this.dataset.timerDuration || 0) * 60));
      const render = () => {
        const minutes = Math.floor(this.remainingSeconds / 60);
        const seconds = this.remainingSeconds % 60;
        const minutesNode = this.querySelector('.popup-modal__timer__minutes');
        const secondsNode = this.querySelector('.popup-modal__timer__seconds');
        if (minutesNode) minutesNode.textContent = String(minutes).padStart(2, '0');
        if (secondsNode) secondsNode.textContent = String(seconds).padStart(2, '0');
        if (this.remainingSeconds <= 0) {
          window.clearInterval(this.countdownTimer);
          return;
        }
        this.remainingSeconds -= 1;
      };
      render();
      this.countdownTimer = window.setInterval(render, 1000);
    }

    disconnectedCallback() {
      window.clearTimeout(this.openTimer);
      window.clearInterval(this.countdownTimer);
      this.querySelectorAll('.promp-popup__close-btn').forEach((button) => button.removeEventListener('click', this.closeHandler));
    }
  }
  define('promo-popup', PromoPopup);

  class SectionGroup extends HTMLElement {
    connectedCallback() {
      if (this.dataset.runtimeBound) return;
      this.dataset.runtimeBound = 'true';
      this.mountSection(this.dataset.sectionOneId, '.section-group__section-one-container');
      this.mountSection(this.dataset.sectionTwoId, '.section-group__section-two-container');
    }

    mountSection(sectionId, containerSelector) {
      if (!sectionId) return;
      const escapedId = window.CSS?.escape ? CSS.escape(String(sectionId)) : String(sectionId);
      const source = document.getElementById(sectionId)
        || document.getElementById(`shopify-section-${sectionId}`)
        || document.querySelector(`[data-section-id="${escapedId}"]`);
      const container = this.querySelector(containerSelector);
      if (!source || !container || source === this || this.contains(source)) return;
      container.append(source);
    }
  }
  define('section-group', SectionGroup);

  class StickyAtc extends HTMLElement {
    connectedCallback() {
      if (this.dataset.runtimeBound) return;
      this.dataset.runtimeBound = 'true';
      this.productForm = document.getElementById(`ProductForm-${this.dataset.section}`)
        || document.querySelector(`product-form[data-section="${this.dataset.section}"]`);
      this.form = document.getElementById(`product-form-${this.dataset.section}`)
        || this.productForm?.querySelector('form[data-type="add-to-cart-form"]');
      this.mainButton = document.getElementById(`ProductSubmitButton-${this.dataset.section}`)
        || this.productForm?.querySelector('[name="add"][type="submit"]');
      this.button = this.querySelector('.sticky-atc__add_button');
      this.scrollButton = this.querySelector('.sticky-atc__scroll-btn');
      this.scrollButton?.addEventListener('click', (event) => this.scrollToDestination(event));
      this.button?.addEventListener('click', (event) => {
        this.prepareVariantForSubmit();
        if (this.mainButton && this.button.type === 'button') {
          event.preventDefault();
          this.form?.requestSubmit
            ? this.form.requestSubmit(this.mainButton)
            : this.mainButton.click();
        }
      });
      this.addEventListener('change', (event) => this.syncVariant(event.target));
      if (this.dataset.afterScroll === 'true') this.observeMainButton();
      if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS?.variantChange) {
        this.variantUnsubscriber = window.subscribe(window.PUB_SUB_EVENTS.variantChange, (event) => {
          const data = event?.data;
          if (!data || String(data.sectionId) !== String(this.dataset.section)) return;
          this.syncFromMainVariant(data.variant, data.html);
        });
      }
    }

    observeMainButton() {
      this.onScroll = () => {
        const passedMainButton = Boolean(this.mainButton)
          && this.mainButton.getBoundingClientRect().bottom <= 0;
        this.classList.toggle('active', passedMainButton);
        this.setAttribute('aria-hidden', String(!passedMainButton));
        this.inert = !passedMainButton;
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      this.onScroll();
    }

    syncVariant(control) {
      if (!control.matches('select')) return;
      const picker = control.closest('[data-section]');
      const variants = parseJsonScript(picker || this);
      const combinedId = control.selectedOptions[0]?.dataset.variantId;
      const variant = combinedId
        ? variants.find((item) => String(item.id) === String(combinedId))
        : findVariant(variants, selectedValues(picker || this));
      if (!variant) return;
      const mainId = this.form?.querySelector('input[name="id"]');
      if (mainId) {
        mainId.value = variant.id;
        mainId.dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (this.form) this.form.dataset.submitVariantId = String(variant.id);
      if (this.button) this.button.disabled = !variant.available;
      dispatch(this, 'variant:change', { variant });
    }

    prepareVariantForSubmit() {
      if (!this.form) return;
      const picker = this.querySelector('[id^="StickyAtcVariantPicker-"]');
      if (!picker) {
        delete this.form.dataset.submitVariantId;
        return;
      }
      const variants = parseJsonScript(picker);
      const combinedId = picker.querySelector('.sticky-atc__variant-select')
        ?.selectedOptions?.[0]?.dataset.variantId;
      const variant = combinedId
        ? variants.find((item) => String(item.id) === String(combinedId))
        : findVariant(variants, selectedValues(picker));
      if (variant?.id) this.form.dataset.submitVariantId = String(variant.id);
    }

    syncFromMainVariant(variant, html) {
      if (!variant) return;
      const picker = this.querySelector('[id^="StickyAtcVariantPicker-"]');
      if (picker) {
        const combined = picker.querySelector('.sticky-atc__variant-select');
        if (combined) {
          const option = Array.from(combined.options).find(
            (item) => String(item.dataset.variantId) === String(variant.id)
          );
          if (option) combined.value = option.value;
        } else {
          Array.from(picker.querySelectorAll('select')).forEach((select, index) => {
            if (variant.options?.[index] != null) select.value = variant.options[index];
          });
        }
      }

      if (this.form) delete this.form.dataset.submitVariantId;
      if (this.button) this.button.disabled = !variant.available;

      const source = html?.querySelector?.(`#sticky-atc-${this.dataset.section}`);
      if (!source) return;
      const selectors = [
        '.sticky-atc__price',
        '.sticky-atc__button-price',
        '.sticky-atc__label__text',
      ];
      selectors.forEach((selector) => {
        const destination = this.querySelector(selector);
        const replacement = source.querySelector(selector);
        if (destination && replacement) destination.innerHTML = replacement.innerHTML;
      });
      const destinationImage = this.querySelector('.sticky-atc__image img');
      const sourceImage = source.querySelector('.sticky-atc__image img');
      if (destinationImage && sourceImage) {
        destinationImage.src = sourceImage.src;
        destinationImage.alt = sourceImage.alt;
      }
    }

    scrollToDestination(event) {
      event.preventDefault();
      let selector = String(this.dataset.scrollDestination || '').trim();
      if (!selector) return;
      selector = selector.replace(/-id\b/g, `-${this.dataset.section}`);
      let target = document.getElementById(selector.replace(/^#/, ''));
      if (!target) {
        try { target = document.querySelector(selector); } catch (_error) { target = null; }
      }
      target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    disconnectedCallback() {
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
      if (this.onScroll) window.removeEventListener('resize', this.onScroll);
      this.variantUnsubscriber?.();
    }
  }
  define('sticky-atc', StickyAtc);

  class BundleDeals extends HTMLElement {
    connectedCallback() {
      if (this.dataset.runtimeBound) return;
      this.dataset.runtimeBound = 'true';
      this.addEventListener('change', (event) => {
        if (event.target.matches('.bundle-deals__checkbox-js')) event.target.dataset.checked = String(event.target.checked);
        if (event.target.matches('.bundle-deals__variant-selects-js select')) this.updateVariant(event.target.closest('.bundle-deals__variant-selects-js'));
        this.refresh();
      });
      this.refresh();
    }

    updateVariant(container) {
      const variants = parseJsonScript(container);
      const variant = findVariant(variants, selectedValues(container));
      if (!variant) return;
      const index = container.dataset.index;
      const checkbox = this.querySelector(`.bundle-deals__checkbox-js[data-index="${index}"]`);
      if (!checkbox) return;
      checkbox.dataset.id = variant.id;
      checkbox.dataset.price = Number(variant.price || 0);
      checkbox.dataset.comparePrice = Math.max(Number(variant.compare_at_price || 0), Number(variant.price || 0));
      checkbox.disabled = !variant.available;
      if (!variant.available) checkbox.checked = false;
    }

    refresh() {
      const selected = Array.from(this.querySelectorAll('.bundle-deals__checkbox-js:checked:not(:disabled)'));
      const total = selected.reduce((sum, item) => sum + Number(item.dataset.price || 0), 0);
      const compare = selected.reduce((sum, item) => sum + Number(item.dataset.comparePrice || 0), 0);
      const priceNode = this.querySelector('.bundle-deals__total-price-js');
      const compareNode = this.querySelector('.bundle-deals__total-compare-price-js');
      if (priceNode) priceNode.textContent = formatMoney(total, this.dataset.moneyFormat);
      if (compareNode) {
        compareNode.textContent = compare > total ? formatMoney(compare, this.dataset.moneyFormat) : '';
        compareNode.hidden = compare <= total;
      }
      const variantsHost = this.querySelector('.product-form__variants');
      if (variantsHost) {
        variantsHost.innerHTML = '';
        selected.forEach((item) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = 'items[][id]';
          input.value = item.dataset.id;
          variantsHost.append(input);
          const quantity = document.createElement('input');
          quantity.type = 'hidden';
          quantity.name = 'items[][quantity]';
          quantity.value = '1';
          variantsHost.append(quantity);
        });
      }
      const submit = this.querySelector('[type="submit"]');
      if (submit) submit.disabled = selected.length === 0;
      dispatch(this, 'bundle:change', { items: selected.map((item) => Number(item.dataset.id)), total });
    }
  }
  define('bundle-deals', BundleDeals);

  class CartDrawerGift extends HTMLElement {
    connectedCallback() {
      if (this.dataset.runtimeBound) return;
      this.dataset.runtimeBound = 'true';
      this.onCartUpdated = (event) => this.update(event.detail.cart);
      document.addEventListener('cart:updated', this.onCartUpdated);
      this.querySelectorAll('.upsell__variant-picker select').forEach((select) => {
        select.addEventListener('change', () => this.updateVariant());
      });
      this.updateVariant();
      this.update();
    }

    updateVariant() {
      const variants = parseJsonScript(this);
      if (!variants.length) return;
      const picker = this.querySelector('.upsell__variant-picker') || this;
      const variant = resolveSelectableVariant(picker, variants, {
        skipUnavailable: true,
        skipNonExistent: true,
      });
      if (!variant || !variant.available) {
        this.dataset.variantAvailable = 'false';
        return;
      }
      this.dataset.variantAvailable = 'true';
      this.dataset.id = String(variant.id);
      const input = this.querySelector('input[name="id"]');
      if (input) input.value = String(variant.id);
      this.update();
    }

    setUnlocked(unlocked) {
      this.dataset.selected = String(unlocked);
      this.classList.toggle('is-unlocked', unlocked);
    }

    update(cart = null) {
      if (this.updatePromise) return this.updatePromise;
      const drawer = this.closest('cart-drawer') || document.querySelector('cart-drawer');
      const task = async () => {
        try {
          return await this.reconcileGift(cart);
        } catch (error) {
          console.error(error);
          dispatch(this, 'cart:error', { message: error.message });
          return { changed: false, cart: null, error };
        }
      };
      this.updatePromise = window.Shopify?.designMode || !drawer?.enqueueCartMutation
        ? task()
        : drawer.enqueueCartMutation(task);
      const clear = () => { this.updatePromise = null; };
      this.updatePromise.then(clear, clear);
      return this.updatePromise;
    }

    getGiftItems(cart) {
      const key = String(this.dataset.giftKey || '');
      return (cart?.items || []).filter((item) => itemProperty(item, '_theme_gift_key') === key);
    }

    findGiftMismatch(cart, unlocked) {
      const giftItems = this.getGiftItems(cart);
      const desiredId = Number(this.dataset.id);
      const desiredItem = giftItems.find((item) => Number(item.variant_id || item.id) === desiredId);
      const staleItem = giftItems.find((item) => Number(item.variant_id || item.id) !== desiredId);
      if (!unlocked && giftItems[0]) return { action: 'remove', cartItem: giftItems[0] };
      if (unlocked && !desiredItem) return { action: 'add' };
      if (unlocked && desiredItem && Number(desiredItem.quantity || 0) !== 1) {
        return { action: 'change', cartItem: desiredItem, quantity: 1 };
      }
      if (unlocked && staleItem) return { action: 'remove', cartItem: staleItem };
      return null;
    }

    async reconcileGift(initialCart = null) {
      let state = initialCart?.items ? initialCart : await this.fetchCart();
      const giftItems = this.getGiftItems(state);
      const primaryItems = (state.items || []).filter((item) => !(
        itemProperty(item, '_theme_upsell_key')
        || itemProperty(item, '_theme_gift_key')
        || itemProperty(item, '_theme_additional_key')
      ));
      const basis = this.dataset.type === 'product_quantity'
        ? primaryItems.reduce((sum, item) => sum + Number(item.quantity || 0), 0)
        : primaryItems.reduce((sum, item) => sum + Number(item.final_line_price ?? item.line_price ?? 0), 0);
      const unlocked = primaryItems.length > 0
        && this.dataset.variantAvailable !== 'false'
        && basis >= Number(this.dataset.requirement || 0);
      this.setUnlocked(unlocked);

      if (window.Shopify?.designMode) return { changed: false, cart: state };
      let mismatch = this.findGiftMismatch(state, unlocked);
      let changed = false;
      let attemptsRemaining = 6;
      while (mismatch && attemptsRemaining > 0) {
        const result = await this.applyGiftMismatch(mismatch);
        state = mismatch.action !== 'add' && isFullCart(result) ? result : await this.fetchCart();
        changed = true;
        attemptsRemaining -= 1;
        mismatch = this.findGiftMismatch(state, unlocked);
      }
      if (mismatch) throw new Error('Gift cart state could not be synchronized.');
      return { changed, cart: state };
    }

    async fetchCart() {
      const response = await fetch(`${window.routes.cart_url.replace(/\/$/, '')}.js`, {
        headers: { Accept: 'application/json' },
      });
      const state = await parseJsonResponse(response, 'Cart could not be loaded.');
      if (!response.ok || state.status) throw new Error(state.description || state.message || 'Cart could not be loaded.');
      return state;
    }

    async applyGiftMismatch(mismatch) {
      this.dataset.loading = 'true';
      try {
        const body = mismatch.action === 'add'
          ? {
              items: [{
                id: Number(this.dataset.id),
                quantity: 1,
                properties: { _theme_gift_key: String(this.dataset.giftKey || '') },
              }],
              sections: ['cart-drawer', 'cart-icon-bubble'],
              sections_url: window.location.pathname,
            }
          : {
              id: mismatch.cartItem?.key,
              quantity: mismatch.action === 'change' ? Number(mismatch.quantity || 1) : 0,
              sections: ['cart-drawer', 'cart-icon-bubble'],
              sections_url: window.location.pathname,
            };
        if (mismatch.action !== 'add' && !body.id) throw new Error('Gift cart item could not be identified.');
        const response = await postJson(
          mismatch.action === 'add' ? window.routes.cart_add_url : window.routes.cart_change_url,
          body
        );
        const state = await parseJsonResponse(response, 'Gift cart update failed.');
        if (!response.ok || state.status) throw new Error(state.description || state.message || 'Gift cart update failed.');
        document.querySelector('cart-drawer')?.renderContents(state, { reconcile: false });
        return state;
      } finally {
        this.dataset.loading = 'false';
      }
    }

    disconnectedCallback() {
      document.removeEventListener('cart:updated', this.onCartUpdated);
    }
  }
  define('cart-drawer-gift', CartDrawerGift);
})();
